import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Users, TrendingUp, Target, Award, Download, ChevronDown, Filter, Calendar, ArrowUpRight, ArrowDownRight, Minus, Zap, MessageCircle, Send, ThumbsUp, AlertTriangle, TrendingDown, Clock, CheckCircle, Star, Mail, Video, Sparkles, BarChart3, Home, UserCog, FileText, Bell, X } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend } from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';
// Mock data for demonstration
const mockLearners = [{
  id: 1,
  name: 'Linda Taylor',
  email: 'linda.taylor@example.com',
  assessments: 3,
  avgScore: 85,
  lastAttempt: '2024-01-15'
}, {
  id: 2,
  name: 'John Smith',
  email: 'john.smith@example.com',
  assessments: 2,
  avgScore: 72,
  lastAttempt: '2024-01-14'
}, {
  id: 3,
  name: 'Sarah Johnson',
  email: 'sarah.j@example.com',
  assessments: 4,
  avgScore: 91,
  lastAttempt: '2024-01-16'
}, {
  id: 4,
  name: 'Michael Chen',
  email: 'mchen@example.com',
  assessments: 3,
  avgScore: 78,
  lastAttempt: '2024-01-13'
}, {
  id: 5,
  name: 'Emily Davis',
  email: 'emily.d@example.com',
  assessments: 2,
  avgScore: 88,
  lastAttempt: '2024-01-15'
}];
const moduleData = [{
  module: 'HTML Basics',
  mastery: 85,
  misinformation: 15,
  avgScore: 82,
  attempts: 45
}, {
  module: 'CSS Fundamentals',
  mastery: 78,
  misinformation: 22,
  avgScore: 76,
  attempts: 42
}, {
  module: 'JavaScript Basics',
  mastery: 72,
  misinformation: 28,
  avgScore: 71,
  attempts: 38
}, {
  module: 'Responsive Design',
  mastery: 80,
  misinformation: 20,
  avgScore: 79,
  attempts: 35
}];
const scoreDistribution = [{
  range: '0-20%',
  count: 2
}, {
  range: '21-40%',
  count: 5
}, {
  range: '41-60%',
  count: 8
}, {
  range: '61-80%',
  count: 15
}, {
  range: '81-100%',
  count: 12
}];
const attemptTrends = [{
  attempt: '1st',
  avgScore: 68
}, {
  attempt: '2nd',
  avgScore: 75
}, {
  attempt: '3rd',
  avgScore: 82
}, {
  attempt: '4th',
  avgScore: 86
}];
const individualModulePerformance = [{
  module: 'HTML Basics',
  score: 92,
  mastery: 95,
  misinformation: 5,
  attempts: 2
}, {
  module: 'CSS Fundamentals',
  score: 85,
  mastery: 88,
  misinformation: 12,
  attempts: 1
}, {
  module: 'JavaScript Basics',
  score: 78,
  mastery: 75,
  misinformation: 25,
  attempts: 3
}, {
  module: 'Responsive Design',
  score: 88,
  mastery: 90,
  misinformation: 10,
  attempts: 1
}];
// Action items mock data
const actionItems = [{
  id: 1,
  type: 'urgent',
  category: 'struggling',
  learner: {
    name: 'John Smith',
    email: 'john.smith@example.com',
    avatar: 'JS'
  },
  insight: 'Failed JavaScript Basics 3 times with declining scores',
  details: 'Score dropped from 68% → 65% → 58% over 3 attempts. High misinformation rate (45%) on core concepts.',
  module: 'JavaScript Basics',
  lastActivity: '2 days ago',
  priority: 'high',
  suggestedActions: ['Schedule 1-on-1 check-in', 'Recommend prerequisite review', 'Assign peer mentor']
}, {
  id: 2,
  type: 'macro',
  category: 'skill-issue',
  scope: 'system',
  insight: 'CSS Fundamentals has 68% failure rate across all learners',
  details: '42 of 62 learners (68%) are failing CSS Fundamentals on first attempt. Average score is 54%, well below the 70% passing threshold. Common struggle areas: Flexbox (82% error rate), Grid Layout (76% error rate), and Positioning (71% error rate).',
  module: 'CSS Fundamentals',
  affectedLearners: 42,
  lastActivity: 'Ongoing',
  priority: 'high',
  suggestedActions: ['Review skill content difficulty', 'Add supplemental Flexbox tutorials', 'Create interactive Grid exercises', 'Schedule instructor review session']
}, {
  id: 3,
  type: 'celebrate',
  category: 'high-performer',
  learner: {
    name: 'Sarah Johnson',
    email: 'sarah.j@example.com',
    avatar: 'SJ'
  },
  insight: 'Achieved 95%+ on all 4 assessments on first attempt',
  details: 'Consistent high performance with strong mastery across all modules. Completed skill 2 weeks ahead of schedule.',
  module: 'All Modules',
  lastActivity: '1 day ago',
  priority: 'positive',
  suggestedActions: ['Send congratulations', 'Invite to mentor program', 'Recommend advanced track']
}, {
  id: 4,
  type: 'macro',
  category: 'low-engagement',
  scope: 'system',
  insight: 'Responsive Design skill has only 12% enrollment rate',
  details: 'Only 30 of 248 active learners (12%) have started Responsive Design, despite it being a core requirement. Skill has been available for 45 days. Learners cite "intimidating title" and "unclear prerequisites" as barriers in feedback surveys.',
  module: 'Responsive Design',
  affectedLearners: 218,
  lastActivity: 'Last 45 days',
  priority: 'medium',
  suggestedActions: ['Rename skill to be more approachable', 'Create promotional campaign', 'Add clear prerequisite guidance', 'Send targeted enrollment emails']
}, {
  id: 5,
  type: 'attention',
  category: 'at-risk',
  learner: {
    name: 'Michael Chen',
    email: 'mchen@example.com',
    avatar: 'MC'
  },
  insight: 'No activity for 7 days, last score was 62%',
  details: 'Was on track but has been inactive. Last assessment showed struggle with CSS layouts.',
  module: 'CSS Fundamentals',
  lastActivity: '7 days ago',
  priority: 'medium',
  suggestedActions: ['Send check-in message', 'Share additional resources', 'Extend deadline']
}, {
  id: 6,
  type: 'macro',
  category: 'content-gap',
  scope: 'system',
  insight: 'High misinformation rate (34%) on JavaScript async concepts',
  details: 'Across 156 attempts on JavaScript Basics, async/await and Promises questions show 34% misinformation rate (learners confidently selecting wrong answers). This is 2.8x higher than the skill average of 12%. Suggests content may be introducing misconceptions rather than clarifying concepts.',
  module: 'JavaScript Basics',
  affectedLearners: 89,
  lastActivity: 'Last 30 days',
  priority: 'high',
  suggestedActions: ['Audit async/await lesson content', 'Add visual diagrams for event loop', 'Create hands-on async exercises', 'Review question phrasing for clarity']
}, {
  id: 7,
  type: 'celebrate',
  category: 'improvement',
  learner: {
    name: 'Emily Davis',
    email: 'emily.d@example.com',
    avatar: 'ED'
  },
  insight: 'Improved from 65% to 88% after focused practice',
  details: 'Showed significant improvement on HTML Basics retake. Mastery increased from 60% to 92%.',
  module: 'HTML Basics',
  lastActivity: '1 day ago',
  priority: 'positive',
  suggestedActions: ['Send encouragement', 'Highlight progress', 'Share success story']
}, {
  id: 8,
  type: 'macro',
  category: 'success-pattern',
  scope: 'system',
  insight: 'HTML Basics completion rate increased 23% after video additions',
  details: 'After adding supplemental video tutorials 3 weeks ago, HTML Basics first-attempt pass rate jumped from 64% to 87%. Average completion time decreased from 4.2 to 3.1 attempts. Learner feedback mentions "videos made concepts click" (mentioned in 78% of positive reviews).',
  module: 'HTML Basics',
  affectedLearners: 124,
  lastActivity: 'Last 3 weeks',
  priority: 'positive',
  suggestedActions: ['Apply video strategy to other skills', 'Document what made videos effective', 'Share success with content team', 'Create video templates for consistency']
}, {
  id: 9,
  type: 'urgent',
  category: 'struggling',
  learner: {
    name: 'Alex Rivera',
    email: 'alex.r@example.com',
    avatar: 'AR'
  },
  insight: 'Consistently scoring below 50% across all modules',
  details: 'Attempted 5 assessments with average score of 45%. High misinformation rate suggests fundamental gaps.',
  module: 'Multiple Modules',
  lastActivity: '3 days ago',
  priority: 'high',
  suggestedActions: ['Schedule urgent meeting', 'Assess learning needs', 'Create custom plan']
}, {
  id: 10,
  type: 'macro',
  category: 'time-pattern',
  scope: 'system',
  insight: 'Weekend attempts have 41% higher failure rate than weekday',
  details: 'Analysis of 892 assessment attempts shows weekend attempts (Sat-Sun) have 41% higher failure rate (58% vs 41%). Possible causes: less instructor support available, learners rushing through content, or fatigue from weekday work. Pattern consistent across all skills.',
  module: 'All Skills',
  affectedLearners: 187,
  lastActivity: 'Last 60 days',
  priority: 'medium',
  suggestedActions: ['Add weekend office hours', 'Send "take your time" reminders on Fridays', 'Analyze if weekend learners need different support', 'Consider weekend-specific resources']
}, {
  id: 11,
  type: 'attention',
  category: 'engagement',
  learner: {
    name: 'Lisa Wong',
    email: 'lisa.w@example.com',
    avatar: 'LW'
  },
  insight: 'Taking unusually long between attempts (10+ days)',
  details: 'Scores are adequate (75% avg) but engagement pattern suggests time management issues.',
  module: 'Responsive Design',
  lastActivity: '5 days ago',
  priority: 'medium',
  suggestedActions: ['Check on progress', 'Offer time management tips', 'Discuss barriers']
}];
export function AnalyticsDashboard() {
  const [selectedLearner, setSelectedLearner] = useState<number | null>(null);
  const [isLearnerDropdownOpen, setIsLearnerDropdownOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState<string>('All Skills Combined');
  const [isSkillDropdownOpen, setIsSkillDropdownOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const skillDropdownRef = useRef<HTMLDivElement>(null);
  const learnerDropdownRef = useRef<HTMLDivElement>(null);
  const skillOptions = ['All Skills Combined', 'HTML Basics', 'CSS Fundamentals', 'JavaScript Basics', 'Responsive Design'];
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (skillDropdownRef.current && !skillDropdownRef.current.contains(event.target as Node)) {
        setIsSkillDropdownOpen(false);
      }
      if (learnerDropdownRef.current && !learnerDropdownRef.current.contains(event.target as Node)) {
        setIsLearnerDropdownOpen(false);
      }
    };
    if (isUserMenuOpen || isSkillDropdownOpen || isLearnerDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen, isSkillDropdownOpen, isLearnerDropdownOpen]);
  const totalLearners = mockLearners.length;
  const totalAssessments = mockLearners.reduce((sum, l) => sum + l.assessments, 0);
  const overallAvgScore = Math.round(mockLearners.reduce((sum, l) => sum + l.avgScore, 0) / totalLearners);
  const overallMastery = Math.round(moduleData.reduce((sum, m) => sum + m.mastery, 0) / moduleData.length);
  // Derive viewMode from state
  const viewMode = selectedLearner ? 'individual' : 'aggregate';
  const selectedLearnerData = selectedLearner ? mockLearners.find(l => l.id === selectedLearner) : null;
  const COLORS = ['#30718D', '#007558', '#FBBC04', '#DB3725'];
  return <div className="flex-1 bg-gray-50 min-h-screen" data-id="element-60">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200" data-id="element-61">
        <div className="max-w-[1440px] mx-auto px-8" data-id="element-62">
          <div className="flex items-center h-16" data-id="element-63">
            {/* Logo */}
            <div className="text-[#DB3725] font-bold text-xl" data-id="element-64">LOGO</div>

            {/* Navigation Links */}
            <nav className="flex items-center gap-8 ml-12" data-id="element-65">
              <Link to="/admin" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-66">
                <Home size={18} data-id="element-67" />
                <span data-id="element-68">Admin Hub</span>
              </Link>
              <Link to="/analytics" className="flex items-center gap-2 text-[#30718D] border-b-2 border-[#30718D] pb-[22px] pt-[22px] transition-colors text-sm font-medium" data-id="element-69">
                <BarChart3 size={18} data-id="element-70" />
                <span data-id="element-71">Analytics</span>
              </Link>
              <Link to="/feedback" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-72">
                <FileText size={18} data-id="element-73" />
                <span data-id="element-74">Reports</span>
              </Link>
              <Link to="/assessment" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-75">
                <UserCog size={18} data-id="element-76" />
                <span data-id="element-77">Users</span>
              </Link>
            </nav>

            {/* Right Side - User Info */}
            <div className="flex items-center gap-4 ml-auto" data-id="element-78">
              <Link to="/alerts" className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-79">
                <Bell size={20} data-id="element-80" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-81"></span>
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-82">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-83">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-84">
                    LT
                  </div>
                  <span className="text-sm font-medium text-gray-900" data-id="element-85">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-86" />
                </button>

                {/* User Dropdown Menu */}
                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-87">
                    <div className="py-2" data-id="element-88">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-89">
                        <Users size={18} className="text-gray-600" data-id="element-90" />
                        <span className="font-medium text-gray-900" data-id="element-91">
                          Profile
                        </span>
                      </Link>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-92">
                        <Home size={18} className="text-gray-600" data-id="element-93" />
                        <span className="font-medium text-gray-900" data-id="element-94">
                          Learner Hub
                        </span>
                      </button>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-95">
                        <UserCog size={18} className="text-gray-600" data-id="element-96" />
                        <span className="font-medium text-gray-900" data-id="element-97">
                          Admin Hub
                        </span>
                      </button>
                      <Link to="/settings" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-98">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-99">
                          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" data-id="element-100"></path>
                          <circle cx="12" cy="12" r="3" data-id="element-101"></circle>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-102">
                          Settings
                        </span>
                      </Link>
                      <div className="border-t border-gray-200 my-2" data-id="element-103"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-104">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-105">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" data-id="element-106"></path>
                          <polyline points="16 17 21 12 16 7" data-id="element-107"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12" data-id="element-108"></line>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-109">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="max-w-[1440px] mx-auto px-8 py-8" data-id="element-110">
        <div className="flex items-center justify-between mb-6" data-id="element-111">
          <div data-id="element-112">
            <h1 className="text-3xl font-bold text-gray-900" data-id="element-113">
              Analytics Dashboard
            </h1>
            <p className="text-gray-600 mt-1" data-id="element-114">
              Track learner progress and assessment performance
            </p>
          </div>
          <div className="flex items-center gap-3" data-id="element-115">
            <button className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium bg-white" data-id="element-116">
              <Calendar size={18} data-id="element-117" />
              <span data-id="element-118">Last 30 Days</span>
              <ChevronDown size={16} data-id="element-119" />
            </button>
            <button className="flex items-center gap-2 px-4 py-2.5 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors text-sm font-medium shadow-sm" data-id="element-120">
              <Download size={18} data-id="element-121" />
              <span data-id="element-122">Export Report</span>
            </button>
          </div>
        </div>

        {/* Filter Dropdowns Row */}
        <div className="flex items-center gap-4" data-id="element-123">
          {/* Learner Dropdown */}
          <div className="relative" ref={learnerDropdownRef} data-id="element-124">
            <button onClick={() => {
            setIsLearnerDropdownOpen(!isLearnerDropdownOpen);
            setIsSkillDropdownOpen(false);
          }} className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-white min-w-[250px] text-sm font-medium" data-id="element-125">
              <Users size={18} className="text-[#30718D]" data-id="element-126" />
              <span className="flex-1 text-left" data-id="element-127">
                {selectedLearnerData ? selectedLearnerData.name : 'All Learners'}
              </span>
              <ChevronDown size={16} className={`transition-transform ${isLearnerDropdownOpen ? 'rotate-180' : ''}`} data-id="element-128" />
            </button>

            {isLearnerDropdownOpen && <div className="absolute top-full mt-2 left-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-80 overflow-y-auto" data-id="element-129">
                {/* All Learners option */}
                <button onClick={() => {
              setSelectedLearner(null);
              setIsLearnerDropdownOpen(false);
            }} className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 text-sm ${selectedLearner === null ? 'bg-[#30718D]/5 text-[#30718D] font-semibold' : 'text-gray-700'}`} data-id="element-130">
                  All Learners
                </button>
                {/* Individual learners sorted by last name */}
                {[...mockLearners].sort((a, b) => {
              const lastNameA = a.name.split(' ').slice(-1)[0].toLowerCase();
              const lastNameB = b.name.split(' ').slice(-1)[0].toLowerCase();
              return lastNameA.localeCompare(lastNameB);
            }).map(learner => <button key={learner.id} onClick={() => {
              setSelectedLearner(learner.id);
              setIsLearnerDropdownOpen(false);
            }} className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${selectedLearner === learner.id ? 'bg-[#30718D]/5' : ''}`} data-id="element-131">
                      <div className={`font-medium text-sm ${selectedLearner === learner.id ? 'text-[#30718D]' : 'text-gray-900'}`} data-id="element-132">
                        {learner.name}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-133">
                        {learner.email}
                      </div>
                    </button>)}
              </div>}
          </div>

          {/* Skill Selector Dropdown */}
          <div className="relative" ref={skillDropdownRef} data-id="element-134">
            <button onClick={() => {
            setIsSkillDropdownOpen(!isSkillDropdownOpen);
            setIsLearnerDropdownOpen(false);
          }} className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-white min-w-[220px] text-sm font-medium" data-id="element-135">
              <Target size={18} className="text-[#30718D]" data-id="element-136" />
              <span className="flex-1 text-left" data-id="element-137">{selectedSkill}</span>
              <ChevronDown size={16} className={`transition-transform ${isSkillDropdownOpen ? 'rotate-180' : ''}`} data-id="element-138" />
            </button>

            {isSkillDropdownOpen && <div className="absolute top-full mt-2 left-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10" data-id="element-139">
                {skillOptions.map(skill => <button key={skill} onClick={() => {
              setSelectedSkill(skill);
              setIsSkillDropdownOpen(false);
            }} className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 text-sm ${selectedSkill === skill ? 'bg-[#30718D]/5 text-[#30718D] font-semibold' : 'text-gray-700'}`} data-id="element-140">
                    {skill}
                  </button>)}
              </div>}
          </div>

          {/* Action Items Button */}
          <Link to="/alerts" className="px-4 py-2.5 rounded-lg text-sm font-medium transition-all relative flex items-center gap-2 border bg-white text-gray-700 border-gray-300 hover:bg-gray-50" data-id="element-141">
            <Bell size={16} data-id="element-142" />
            Action Items
            <span className="w-5 h-5 bg-[#DB3725] text-white text-xs font-bold rounded-full flex items-center justify-center shadow-sm" data-id="element-143">
              7
            </span>
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-8 py-6" data-id="element-144">
        {viewMode === 'aggregate' ? <AggregateView totalLearners={totalLearners} totalAssessments={totalAssessments} overallAvgScore={overallAvgScore} overallMastery={overallMastery} moduleData={moduleData} scoreDistribution={scoreDistribution} attemptTrends={attemptTrends} COLORS={COLORS} selectedSkill={selectedSkill} data-id="element-145" /> : selectedLearnerData ? <IndividualView learner={selectedLearnerData} modulePerformance={individualModulePerformance} actionItems={actionItems} COLORS={COLORS} selectedSkill={selectedSkill} data-id="element-146" /> : <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-100" data-id="element-147">
            <Users size={48} className="mx-auto text-gray-400 mb-4" data-id="element-148" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-149">
              Select a Learner
            </h3>
            <p className="text-gray-600" data-id="element-150">
              Choose a learner from the dropdown above to view their individual
              performance
            </p>
          </div>}
      </div>
    </div>;
}
function ActionItemsView({
  actionItems,
  selectedSkill
}) {
  const [activeFilter, setActiveFilter] = useState<'all' | 'urgent' | 'attention' | 'celebrate'>('all');
  const [selectedAction, setSelectedAction] = useState<number | null>(null);
  const [viewType, setViewType] = useState<'all' | 'individual' | 'macro'>('all');
  // Modal states
  const [messageModal, setMessageModal] = useState<{
    isOpen: boolean;
    item: any | null;
  }>({
    isOpen: false,
    item: null
  });
  const [meetingModal, setMeetingModal] = useState<{
    isOpen: boolean;
    item: any | null;
  }>({
    isOpen: false,
    item: null
  });
  const [kudosModal, setKudosModal] = useState<{
    isOpen: boolean;
    item: any | null;
  }>({
    isOpen: false,
    item: null
  });
  // Filter action items by selected skill
  const skillFilteredItems = selectedSkill === 'All Skills Combined' ? actionItems : actionItems.filter(item => item.module === selectedSkill || item.module === 'All Modules' || item.module === 'All Skills' || item.module === 'Multiple Modules');
  const filteredItems = skillFilteredItems.filter(item => {
    // Filter by view type
    if (viewType === 'individual' && item.type === 'macro') return false;
    if (viewType === 'macro' && item.type !== 'macro') return false;
    // Filter by priority
    if (activeFilter === 'all') return true;
    if (activeFilter === 'urgent') return item.priority === 'high';
    if (activeFilter === 'attention') return item.priority === 'medium';
    if (activeFilter === 'celebrate') return item.priority === 'positive';
    return true;
  });
  const urgentCount = skillFilteredItems.filter(a => a.priority === 'high').length;
  const attentionCount = skillFilteredItems.filter(a => a.priority === 'medium').length;
  const celebrateCount = skillFilteredItems.filter(a => a.priority === 'positive').length;
  const macroCount = skillFilteredItems.filter(a => a.type === 'macro').length;
  const individualCount = skillFilteredItems.filter(a => a.type !== 'macro').length;
  // Get recipient name for modals
  const getRecipientName = (item: any) => {
    if (item.learner) return item.learner.name;
    return 'Team';
  };
  const getRecipientFirstName = (item: any) => {
    if (item.learner) return item.learner.name.split(' ')[0];
    return 'Team';
  };
  return <div data-id="element-151">
      {/* Message Modal */}
      <AnimatePresence data-id="element-152">
        {messageModal.isOpen && messageModal.item && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setMessageModal({
        isOpen: false,
        item: null
      })} data-id="element-153">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-white rounded-xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()} data-id="element-154">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-155">
                <div className="flex items-center gap-3" data-id="element-156">
                  <div className="w-12 h-12 rounded-lg bg-[#30718D] flex items-center justify-center" data-id="element-157">
                    <Mail size={24} className="text-white" data-id="element-158" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-159">
                    Send Message
                  </h2>
                </div>
                <button onClick={() => setMessageModal({
              isOpen: false,
              item: null
            })} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-160">
                  <X size={20} className="text-gray-500" data-id="element-161" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6" data-id="element-162">
                <p className="text-gray-600 mb-1" data-id="element-163">
                  Your message has been successfully sent to
                </p>
                <p className="text-lg font-bold text-gray-900 mb-6" data-id="element-164">
                  {getRecipientName(messageModal.item)}
                </p>

                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-165">
                  <p className="text-sm text-gray-500 mb-2" data-id="element-166">Message Preview:</p>
                  <p className="text-gray-700 leading-relaxed" data-id="element-167">
                    "Hi {getRecipientFirstName(messageModal.item)}, I noticed
                    your recent assessment activity and wanted to reach out to
                    discuss your progress. Let's schedule a time to connect."
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="flex gap-3 p-6 border-t border-gray-100" data-id="element-168">
                <button onClick={() => setMessageModal({
              isOpen: false,
              item: null
            })} className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium" data-id="element-169">
                  Cancel
                </button>
                <button onClick={() => setMessageModal({
              isOpen: false,
              item: null
            })} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium" data-id="element-170">
                  <Send size={18} data-id="element-171" />
                  Send
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Meeting Modal */}
      <AnimatePresence data-id="element-172">
        {meetingModal.isOpen && meetingModal.item && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setMeetingModal({
        isOpen: false,
        item: null
      })} data-id="element-173">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-white rounded-xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()} data-id="element-174">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-175">
                <div className="flex items-center gap-3" data-id="element-176">
                  <div className="w-12 h-12 rounded-lg bg-[#30718D] flex items-center justify-center" data-id="element-177">
                    <Video size={24} className="text-white" data-id="element-178" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-179">
                    Schedule Meeting
                  </h2>
                </div>
                <button onClick={() => setMeetingModal({
              isOpen: false,
              item: null
            })} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-180">
                  <X size={20} className="text-gray-500" data-id="element-181" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6" data-id="element-182">
                <p className="text-gray-600 mb-1" data-id="element-183">Schedule a meeting with</p>
                <p className="text-lg font-bold text-gray-900 mb-6" data-id="element-184">
                  {getRecipientName(meetingModal.item)}
                </p>

                <div className="space-y-4" data-id="element-185">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-186">
                    <p className="text-sm text-gray-500 mb-2" data-id="element-187">Meeting Topic:</p>
                    <p className="text-gray-700 font-medium" data-id="element-188">
                      {meetingModal.item.insight}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-189">
                    <div data-id="element-190">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-191">
                        Date
                      </label>
                      <input type="date" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#30718D] focus:border-transparent" defaultValue={new Date().toISOString().split('T')[0]} data-id="element-192" />
                    </div>
                    <div data-id="element-193">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-194">
                        Time
                      </label>
                      <input type="time" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#30718D] focus:border-transparent" defaultValue="10:00" data-id="element-195" />
                    </div>
                  </div>

                  <div data-id="element-196">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-197">
                      Duration
                    </label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#30718D] focus:border-transparent" data-id="element-198">
                      <option data-id="element-199">15 minutes</option>
                      <option selected data-id="element-200">30 minutes</option>
                      <option data-id="element-201">45 minutes</option>
                      <option data-id="element-202">1 hour</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex gap-3 p-6 border-t border-gray-100" data-id="element-203">
                <button onClick={() => setMeetingModal({
              isOpen: false,
              item: null
            })} className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium" data-id="element-204">
                  Cancel
                </button>
                <button onClick={() => setMeetingModal({
              isOpen: false,
              item: null
            })} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium" data-id="element-205">
                  <Calendar size={18} data-id="element-206" />
                  Schedule
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Kudos Modal */}
      <AnimatePresence data-id="element-207">
        {kudosModal.isOpen && kudosModal.item && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setKudosModal({
        isOpen: false,
        item: null
      })} data-id="element-208">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-white rounded-xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()} data-id="element-209">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-210">
                <div className="flex items-center gap-3" data-id="element-211">
                  <div className="w-12 h-12 rounded-lg bg-[#007558] flex items-center justify-center" data-id="element-212">
                    <ThumbsUp size={24} className="text-white" data-id="element-213" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-214">
                    Give Kudos
                  </h2>
                </div>
                <button onClick={() => setKudosModal({
              isOpen: false,
              item: null
            })} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-215">
                  <X size={20} className="text-gray-500" data-id="element-216" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6" data-id="element-217">
                <p className="text-gray-600 mb-1" data-id="element-218">Send kudos to celebrate</p>
                <p className="text-lg font-bold text-gray-900 mb-6" data-id="element-219">
                  {getRecipientName(kudosModal.item)}
                </p>

                <div className="bg-[#007558]/5 rounded-lg p-4 border border-[#007558]/20 mb-4" data-id="element-220">
                  <div className="flex items-center gap-2 mb-2" data-id="element-221">
                    <Star size={18} className="text-[#007558]" data-id="element-222" />
                    <p className="text-sm font-medium text-[#007558]" data-id="element-223">
                      Achievement:
                    </p>
                  </div>
                  <p className="text-gray-700" data-id="element-224">{kudosModal.item.insight}</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-225">
                  <p className="text-sm text-gray-500 mb-2" data-id="element-226">Kudos Message:</p>
                  <p className="text-gray-700 leading-relaxed" data-id="element-227">
                    "Congratulations {getRecipientFirstName(kudosModal.item)}!
                    🎉 Your hard work and dedication have really paid off. Keep
                    up the amazing work!"
                  </p>
                </div>

                <div className="mt-4" data-id="element-228">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-229">
                    Add a personal note (optional)
                  </label>
                  <textarea className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007558] focus:border-transparent resize-none" rows={3} placeholder="Add your personal message..." data-id="element-230" />
                </div>
              </div>

              {/* Footer */}
              <div className="flex gap-3 p-6 border-t border-gray-100" data-id="element-231">
                <button onClick={() => setKudosModal({
              isOpen: false,
              item: null
            })} className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium" data-id="element-232">
                  Cancel
                </button>
                <button onClick={() => setKudosModal({
              isOpen: false,
              item: null
            })} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium" data-id="element-233">
                  <ThumbsUp size={18} data-id="element-234" />
                  Send Kudos
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Skill Filter Indicator */}
      {selectedSkill !== 'All Skills Combined'}

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-6 mb-6" data-id="element-235">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-236">
          <div className="flex items-center justify-between mb-3" data-id="element-237">
            <div className="w-12 h-12 rounded-lg bg-[#DB3725]/10 flex items-center justify-center" data-id="element-238">
              <AlertTriangle size={24} className="text-[#DB3725]" data-id="element-239" />
            </div>
            <span className="text-3xl font-bold text-[#DB3725]" data-id="element-240">
              {urgentCount}
            </span>
          </div>
          <div className="text-sm font-medium text-gray-900" data-id="element-241">
            Urgent Actions
          </div>
          <div className="text-xs text-gray-500 mt-1" data-id="element-242">
            Require immediate attention
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-243">
          <div className="flex items-center justify-between mb-3" data-id="element-244">
            <div className="w-12 h-12 rounded-lg bg-yellow-100 flex items-center justify-center" data-id="element-245">
              <Clock size={24} className="text-yellow-600" data-id="element-246" />
            </div>
            <span className="text-3xl font-bold text-yellow-600" data-id="element-247">
              {attentionCount}
            </span>
          </div>
          <div className="text-sm font-medium text-gray-900" data-id="element-248">
            Needs Attention
          </div>
          <div className="text-xs text-gray-500 mt-1" data-id="element-249">Monitor and support</div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-250">
          <div className="flex items-center justify-between mb-3" data-id="element-251">
            <div className="w-12 h-12 rounded-lg bg-[#007558]/10 flex items-center justify-center" data-id="element-252">
              <Star size={24} className="text-[#007558]" data-id="element-253" />
            </div>
            <span className="text-3xl font-bold text-[#007558]" data-id="element-254">
              {celebrateCount}
            </span>
          </div>
          <div className="text-sm font-medium text-gray-900" data-id="element-255">
            Celebrate Success
          </div>
          <div className="text-xs text-gray-500 mt-1" data-id="element-256">
            Recognize achievements
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-257">
          <div className="flex items-center justify-between mb-3" data-id="element-258">
            <div className="w-12 h-12 rounded-lg bg-[#30718D]/10 flex items-center justify-center" data-id="element-259">
              <CheckCircle size={24} className="text-[#30718D]" data-id="element-260" />
            </div>
            <span className="text-3xl font-bold text-[#30718D]" data-id="element-261">
              {skillFilteredItems.length}
            </span>
          </div>
          <div className="text-sm font-medium text-gray-900" data-id="element-262">Total Actions</div>
          <div className="text-xs text-gray-500 mt-1" data-id="element-263">
            {selectedSkill === 'All Skills Combined' ? 'All recommended items' : `For ${selectedSkill}`}
          </div>
        </motion.div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-6" data-id="element-264">
        <div className="flex items-center justify-between mb-4" data-id="element-265">
          <div className="flex items-center gap-2" data-id="element-266">
            <Filter size={18} className="text-gray-600" data-id="element-267" />
            <span className="text-sm font-medium text-gray-700" data-id="element-268">
              Filter by:
            </span>
            <div className="flex items-center gap-2 ml-2" data-id="element-269">
              <button onClick={() => setActiveFilter('all')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeFilter === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-270">
                All ({skillFilteredItems.length})
              </button>
              <button onClick={() => setActiveFilter('urgent')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeFilter === 'urgent' ? 'bg-[#DB3725] text-white' : 'bg-[#DB3725]/10 text-[#DB3725] hover:bg-[#DB3725]/20'}`} data-id="element-271">
                Urgent ({urgentCount})
              </button>
              <button onClick={() => setActiveFilter('attention')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeFilter === 'attention' ? 'bg-yellow-600 text-white' : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'}`} data-id="element-272">
                Needs Attention ({attentionCount})
              </button>
              <button onClick={() => setActiveFilter('celebrate')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeFilter === 'celebrate' ? 'bg-[#007558] text-white' : 'bg-[#007558]/10 text-[#007558] hover:bg-[#007558]/20'}`} data-id="element-273">
                Celebrate ({celebrateCount})
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-274">
            <span className="text-sm font-medium text-gray-700" data-id="element-275">View:</span>
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1" data-id="element-276">
              <button onClick={() => setViewType('all')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${viewType === 'all' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-277">
                All
              </button>
              <button onClick={() => setViewType('individual')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${viewType === 'individual' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-278">
                Individual ({individualCount})
              </button>
              <button onClick={() => setViewType('macro')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${viewType === 'macro' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-279">
                System-Wide ({macroCount})
              </button>
            </div>
          </div>
        </div>
        <div className="text-sm text-gray-600" data-id="element-280">
          Showing {filteredItems.length} of {skillFilteredItems.length} items
          {selectedSkill !== 'All Skills Combined' && ` (filtered by ${selectedSkill})`}
        </div>
      </div>

      {/* Action Items List */}
      <div className="space-y-4" data-id="element-281">
        <AnimatePresence mode="popLayout" data-id="element-282">
          {filteredItems.map((item, index) => <motion.div key={item.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          delay: index * 0.05
        }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200" data-id="element-283">
              {/* Header Row */}
              <div className="flex items-start justify-between mb-1" data-id="element-284">
                <div className="flex items-center gap-4" data-id="element-285">
                  {/* Icon */}
                  <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${item.priority === 'high' ? 'bg-[#DB3725]/10 border-2 border-[#DB3725]' : item.priority === 'medium' ? 'bg-[#F59E0B]/10 border-2 border-[#F59E0B]' : 'bg-[#007558]/10 border-2 border-[#007558]'}`} data-id="element-286">
                    {item.priority === 'high' ? <AlertTriangle size={24} className="text-[#DB3725]" data-id="element-287" /> : item.priority === 'medium' ? <Clock size={24} className="text-[#F59E0B]" data-id="element-288" /> : <Star size={24} className="text-[#007558]" data-id="element-289" />}
                  </div>

                  {/* Name and Badge */}
                  <div data-id="element-290">
                    <div className="flex items-center gap-3" data-id="element-291">
                      <h4 className="text-lg font-semibold text-gray-900" data-id="element-292">
                        {item.learner ? item.learner.name : 'System Alert'}
                      </h4>
                      <span className={`px-3 py-1 rounded text-xs font-semibold ${item.priority === 'high' ? 'bg-[#DB3725] text-white' : item.priority === 'medium' ? 'bg-white text-gray-900 border border-gray-900' : 'bg-white text-gray-900 border border-gray-900'}`} data-id="element-293">
                        {item.priority === 'high' ? 'Urgent' : item.priority === 'medium' ? 'Attention' : 'Celebrate'}
                      </span>
                    </div>
                    {item.learner && <p className="text-sm text-gray-500 mt-0.5" data-id="element-294">
                        {item.learner.email}
                      </p>}
                    {item.type === 'macro' && <p className="text-sm text-gray-500 mt-0.5" data-id="element-295">
                        {item.affectedLearners} learners affected
                      </p>}
                  </div>
                </div>

                {/* Module and Time */}
                <div className="text-right" data-id="element-296">
                  <p className="font-semibold text-gray-900" data-id="element-297">{item.module}</p>
                  <p className="text-sm text-gray-500" data-id="element-298">{item.lastActivity}</p>
                </div>
              </div>

              {/* Insight */}
              <div className="flex items-start gap-2 mt-4 mb-2" data-id="element-299">
                <Zap size={18} className="text-gray-700 mt-0.5 flex-shrink-0" data-id="element-300" />
                <p className="font-semibold text-gray-900" data-id="element-301">{item.insight}</p>
              </div>

              {/* Details */}
              <p className="text-sm text-gray-600 ml-6 mb-5 leading-relaxed" data-id="element-302">
                {item.details}
              </p>

              {/* Suggested Actions */}
              <div className="mb-5" data-id="element-303">
                <p className="text-sm font-semibold text-gray-700 mb-3" data-id="element-304">
                  Suggested Actions
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-305">
                  {item.suggestedActions.map((action, idx) => <span key={idx} className="px-4 py-2 bg-gray-100 text-gray-800 rounded text-sm font-medium border border-gray-200" data-id="element-306">
                      {action}
                    </span>)}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3 pt-5 border-t border-gray-200" data-id="element-307">
                <button onClick={() => setMessageModal({
              isOpen: true,
              item
            })} className="flex items-center gap-2 px-5 py-2.5 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-all text-sm font-medium" data-id="element-308">
                  <Mail size={16} data-id="element-309" />
                  <span data-id="element-310">Send Message</span>
                </button>
                <button onClick={() => setMeetingModal({
              isOpen: true,
              item
            })} className="flex items-center gap-2 px-5 py-2.5 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-all text-sm font-medium" data-id="element-311">
                  <Video size={16} data-id="element-312" />
                  <span data-id="element-313">Schedule Meeting</span>
                </button>
                {item.priority === 'positive' && <button onClick={() => setKudosModal({
              isOpen: true,
              item
            })} className="flex items-center gap-2 px-5 py-2.5 border border-[#007558] bg-[#007558]/10 text-[#007558] rounded-lg hover:bg-[#007558]/20 transition-all text-sm font-medium" data-id="element-314">
                    <ThumbsUp size={16} data-id="element-315" />
                    <span data-id="element-316">Send Kudos</span>
                  </button>}
                <button className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors" data-id="element-317">
                  Dismiss
                </button>
              </div>
            </motion.div>)}
        </AnimatePresence>
      </div>
    </div>;
}
function AggregateView({
  totalLearners,
  totalAssessments,
  overallAvgScore,
  overallMastery,
  moduleData,
  scoreDistribution,
  attemptTrends,
  COLORS,
  selectedSkill
}) {
  const [activeTab, setActiveTab] = useState<'leaderBoard' | 'assessmentInsights'>('assessmentInsights');
  // Filter module data based on selected skill
  const filteredModuleData = selectedSkill === 'All Skills Combined' ? moduleData : moduleData.filter(m => m.module === selectedSkill);
  // Calculate filtered metrics
  const filteredAvgScore = filteredModuleData.length > 0 ? Math.round(filteredModuleData.reduce((sum, m) => sum + m.avgScore, 0) / filteredModuleData.length) : 0;
  const filteredMastery = filteredModuleData.length > 0 ? Math.round(filteredModuleData.reduce((sum, m) => sum + m.mastery, 0) / filteredModuleData.length) : 0;
  const filteredAttempts = filteredModuleData.reduce((sum, m) => sum + m.attempts, 0);
  // Filter score distribution based on skill (simulated - in real app this would come from backend)
  const filteredScoreDistribution = selectedSkill === 'All Skills Combined' ? scoreDistribution : scoreDistribution.map(item => ({
    ...item,
    count: Math.round(item.count * (filteredModuleData.length / moduleData.length))
  }));
  return <>
      {/* Key Metrics */}
      <div className="grid grid-cols-4 gap-6 mb-6" data-id="element-318">
        <MetricCard icon={<Users size={24} data-id="element-320" />} label="Total Learners" value={selectedSkill === 'All Skills Combined' ? totalLearners : Math.round(totalLearners * 0.8)} change={+12} changeLabel="vs last month" color="blue" data-id="element-319" />
        <MetricCard icon={<Target size={24} data-id="element-322" />} label="Assessments Taken" value={selectedSkill === 'All Skills Combined' ? totalAssessments : filteredAttempts} change={+8} changeLabel="vs last month" color="green" data-id="element-321" />
        <MetricCard icon={<TrendingUp size={24} data-id="element-324" />} label="Average Score" value={`${selectedSkill === 'All Skills Combined' ? overallAvgScore : filteredAvgScore}%`} change={+5} changeLabel="vs last month" color="purple" data-id="element-323" />
        <MetricCard icon={<Award size={24} data-id="element-326" />} label="Mastery Rate" value={`${selectedSkill === 'All Skills Combined' ? overallMastery : filteredMastery}%`} change={+3} changeLabel="vs last month" color="orange" data-id="element-325" />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-2 gap-6 mb-6" data-id="element-327">
        {/* Score Distribution */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-328">
          <h3 className="text-lg font-semibold mb-4" data-id="element-329">
            Score Distribution{' '}
            {selectedSkill !== 'All Skills Combined' && `- ${selectedSkill}`}
          </h3>
          <ResponsiveContainer width="100%" height={300} data-id="element-330">
            <BarChart data={filteredScoreDistribution} data-id="element-331">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-332" />
              <XAxis dataKey="range" data-id="element-333" />
              <YAxis data-id="element-334" />
              <Tooltip data-id="element-335" />
              <Bar dataKey="count" fill="#30718D" radius={[8, 8, 0, 0]} data-id="element-336" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Attempt Trends */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-337">
          <h3 className="text-lg font-semibold mb-4" data-id="element-338">
            Performance by Attempt{' '}
            {selectedSkill !== 'All Skills Combined' && `- ${selectedSkill}`}
          </h3>
          <ResponsiveContainer width="100%" height={300} data-id="element-339">
            <LineChart data={attemptTrends} data-id="element-340">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-341" />
              <XAxis dataKey="attempt" data-id="element-342" />
              <YAxis domain={[0, 100]} data-id="element-343" />
              <Tooltip data-id="element-344" />
              <Line type="monotone" dataKey="avgScore" stroke="#30718D" strokeWidth={3} dot={{
              fill: '#30718D',
              r: 6
            }} data-id="element-345" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Module Performance Table */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6" data-id="element-346">
        <div className="flex items-center justify-between mb-4" data-id="element-347">
          <h3 className="text-lg font-semibold" data-id="element-348">
            {selectedSkill === 'All Skills Combined' ? 'Module-Level Mastery vs Misinformation' : `${selectedSkill} - Mastery vs Misinformation`}
          </h3>
          {selectedSkill !== 'All Skills Combined' && <span className="px-3 py-1 bg-[#30718D]/10 text-[#30718D] rounded-full text-sm font-medium" data-id="element-349">
              Filtered by: {selectedSkill}
            </span>}
        </div>
        <div className="overflow-x-auto" data-id="element-350">
          <table className="w-full" data-id="element-351">
            <thead data-id="element-352">
              <tr className="border-b border-gray-200" data-id="element-353">
                <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700" data-id="element-354">
                  Module
                </th>
                <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700" data-id="element-355">
                  Avg Score
                </th>
                <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700" data-id="element-356">
                  Mastery
                </th>
                <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700" data-id="element-357">
                  Misinformation
                </th>
                <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700" data-id="element-358">
                  Attempts
                </th>
                <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700" data-id="element-359">
                  Visual
                </th>
              </tr>
            </thead>
            <tbody data-id="element-360">
              {filteredModuleData.map((module, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors" data-id="element-361">
                  <td className="py-4 px-4 font-medium" data-id="element-362">{module.module}</td>
                  <td className="py-4 px-4" data-id="element-363">
                    <span className="font-semibold" data-id="element-364">{module.avgScore}%</span>
                  </td>
                  <td className="py-4 px-4" data-id="element-365">
                    <div className="flex items-center gap-2" data-id="element-366">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden max-w-[120px]" data-id="element-367">
                        <div className="h-full bg-[#007558]" style={{
                      width: `${module.mastery}%`
                    }} data-id="element-368" />
                      </div>
                      <span className="text-sm font-medium text-[#007558]" data-id="element-369">
                        {module.mastery}%
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-4" data-id="element-370">
                    <div className="flex items-center gap-2" data-id="element-371">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden max-w-[120px]" data-id="element-372">
                        <div className="h-full bg-[#DB3725]" style={{
                      width: `${module.misinformation}%`
                    }} data-id="element-373" />
                      </div>
                      <span className="text-sm font-medium text-[#DB3725]" data-id="element-374">
                        {module.misinformation}%
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-gray-600" data-id="element-375">{module.attempts}</td>
                  <td className="py-4 px-4" data-id="element-376">
                    <div className="flex gap-1" data-id="element-377">
                      <div className="h-8 bg-[#007558] rounded" style={{
                    width: `${module.mastery}px`
                  }} data-id="element-378" />
                      <div className="h-8 bg-[#DB3725] rounded" style={{
                    width: `${module.misinformation}px`
                  }} data-id="element-379" />
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
          {filteredModuleData.length === 0 && <div className="text-center py-8 text-gray-500" data-id="element-380">
              No data available for the selected skill.
            </div>}
        </div>
      </div>

      {/* Learner Leaderboard / Assessment Insights */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-381">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 px-6 pt-6" data-id="element-382">
          <div className="flex items-center justify-between mb-4" data-id="element-383">
            <div className="flex items-center gap-2" data-id="element-384">
              <button onClick={() => setActiveTab('assessmentInsights')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'assessmentInsights' ? 'bg-[#30718D] text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-385">
                Assessment Insights
              </button>
              <button onClick={() => setActiveTab('leaderBoard')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'leaderBoard' ? 'bg-[#30718D] text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-386">
                Leader Board
              </button>
            </div>
            <h3 className="text-lg font-bold text-gray-900" data-id="element-387">{selectedSkill}</h3>
          </div>
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait" data-id="element-388">
          {activeTab === 'leaderBoard' ? <motion.div key="leaderBoard" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.2
        }} data-id="element-389">
              {/* Leaderboard Header */}
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-390">
                <div className="flex items-center justify-between" data-id="element-391">
                  <div data-id="element-392">
                    <h4 className="text-base font-bold text-gray-900 mb-1" data-id="element-393">
                      Leader Board
                    </h4>
                    <p className="text-sm text-gray-600" data-id="element-394">
                      Leading learners ranked by selected metric
                    </p>
                  </div>
                  <div className="flex items-center gap-6" data-id="element-395">
                    <div className="flex items-center gap-3" data-id="element-396"></div>
                    <div className="flex items-center gap-3" data-id="element-397">
                      <label className="text-sm font-semibold text-gray-700" data-id="element-398">
                        Metric:
                      </label>
                      <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium bg-white focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent" data-id="element-399">
                        <option data-id="element-400">Average Score</option>
                        <option data-id="element-401">Total Assessments</option>
                        <option data-id="element-402">Completion Rate</option>
                        <option data-id="element-403">Mastery Level</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Leaderboard List */}
              <div className="p-6" data-id="element-404">
                <div className="space-y-3" data-id="element-405">
                  {[...mockLearners].sort((a, b) => b.avgScore - a.avgScore).map((learner, index) => {
                const rank = index + 1;
                const isTopThree = rank <= 3;
                const rankColors = {
                  1: 'bg-gradient-to-br from-yellow-400 to-yellow-500 text-white shadow-md',
                  2: 'bg-gradient-to-br from-gray-300 to-gray-400 text-white shadow-md',
                  3: 'bg-gradient-to-br from-orange-400 to-orange-500 text-white shadow-md'
                };
                return <motion.div key={learner.id} initial={{
                  opacity: 0,
                  x: -20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: index * 0.05
                }} className={`flex items-center gap-4 p-4 rounded-lg border transition-all ${isTopThree ? 'bg-gradient-to-r from-gray-50 to-white border-gray-200 shadow-sm' : 'bg-white border-gray-200 hover:bg-gray-50'}`} data-id="element-406">
                          {/* Rank */}
                          <div className="flex-shrink-0" data-id="element-407">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${rankColors[rank] || 'bg-gray-100 text-gray-600'}`} data-id="element-408">
                              {rank}
                            </div>
                          </div>

                          {/* Learner Info */}
                          <div className="flex items-center gap-3 flex-1 min-w-0" data-id="element-409">
                            <div className="w-10 h-10 rounded-full bg-[#30718D] flex items-center justify-center text-white text-sm font-bold flex-shrink-0" data-id="element-410">
                              {learner.name.split(' ').map(n => n[0]).join('')}
                            </div>
                            <div className="min-w-0 flex-1" data-id="element-411">
                              <div className="font-semibold text-gray-900 truncate" data-id="element-412">
                                {learner.name}
                              </div>
                              <div className="text-sm text-gray-500 truncate" data-id="element-413">
                                {learner.email}
                              </div>
                            </div>
                          </div>

                          {/* Stats */}
                          <div className="flex items-center gap-8 flex-shrink-0" data-id="element-414">
                            {/* Average Score - Primary Metric */}
                            <div className="text-center" data-id="element-415">
                              <div className="text-xs font-medium text-gray-500 mb-1" data-id="element-416">
                                Avg Score
                              </div>
                              <div className={`text-2xl font-bold ${isTopThree ? 'text-[#30718D]' : 'text-gray-900'}`} data-id="element-417">
                                {learner.avgScore}%
                              </div>
                            </div>

                            {/* Assessments */}
                            <div className="text-center" data-id="element-418">
                              <div className="text-xs font-medium text-gray-500 mb-1" data-id="element-419">
                                Assessments
                              </div>
                              <div className="text-lg font-semibold text-gray-700" data-id="element-420">
                                {learner.assessments}
                              </div>
                            </div>

                            {/* Last Attempt */}
                            <div className="text-center min-w-[100px]" data-id="element-421">
                              <div className="text-xs font-medium text-gray-500 mb-1" data-id="element-422">
                                Last Attempt
                              </div>
                              <div className="text-sm font-medium text-gray-700" data-id="element-423">
                                {learner.lastAttempt}
                              </div>
                            </div>

                            {/* View Details Button */}
                            <button className="px-4 py-2 text-sm font-medium text-[#30718D] hover:bg-[#30718D]/10 rounded-lg transition-colors" data-id="element-424">
                              View Details
                            </button>
                          </div>
                        </motion.div>;
              })}
                </div>
              </div>
            </motion.div> : <motion.div key="assessmentInsights" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.2
        }} data-id="element-425">
              {/* Assessment Insights Header */}
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-426">
                <h4 className="text-base font-bold text-gray-900 mb-1" data-id="element-427">
                  Skill Performance
                </h4>
                <p className="text-sm text-gray-600" data-id="element-428">
                  {selectedSkill === 'All Skills Combined' ? 'Detailed metrics for all skills' : 'Detailed metrics for the selected skill'}
                </p>
              </div>

              {/* Metrics Grid */}
              <div className="p-6" data-id="element-429">
                <div className="grid grid-cols-4 gap-4 mb-4" data-id="element-430">
                  {/* Row 1 */}
                  <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.1
              }} className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-431">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2" data-id="element-432">
                      Learners Attempted
                    </div>
                    <div className="text-4xl font-bold text-gray-900 mb-2" data-id="element-433">
                      86
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-434">
                      Total number of learners who attempted the assessment for
                      skill
                    </div>
                  </motion.div>

                  <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.15
              }} className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-435">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2" data-id="element-436">
                      Learners Passed
                    </div>
                    <div className="text-4xl font-bold text-gray-900 mb-2" data-id="element-437">
                      85
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-438">
                      Number of learners who successfully passed the skill
                    </div>
                  </motion.div>

                  <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.2
              }} className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-439">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2" data-id="element-440">
                      Completion Rate
                    </div>
                    <div className="text-4xl font-bold text-gray-900 mb-2" data-id="element-441">
                      98.33%
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-442">
                      Percentage of learners who passed the assessment
                    </div>
                  </motion.div>

                  <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.25
              }} className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-443">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2" data-id="element-444">
                      Avg. Number of Attempts
                    </div>
                    <div className="text-4xl font-bold text-gray-900 mb-2" data-id="element-445">
                      3.78
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-446">
                      Average number of attempts per learner to complete the
                      skill
                    </div>
                  </motion.div>
                </div>

                <div className="grid grid-cols-4 gap-4" data-id="element-447">
                  {/* Row 2 */}
                  <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.3
              }} className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-448">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2" data-id="element-449">
                      Avg. Final Score
                    </div>
                    <div className="text-4xl font-bold text-gray-900 mb-2" data-id="element-450">
                      92.25%
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-451">
                      Average final score achieved by all learners
                    </div>
                  </motion.div>

                  <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.35
              }} className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-452">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2" data-id="element-453">
                      Avg. First Score
                    </div>
                    <div className="text-4xl font-bold text-gray-900 mb-2" data-id="element-454">
                      64.93%
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-455">
                      Average score achieved on first attempt
                    </div>
                  </motion.div>

                  <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.4
              }} className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-456">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2" data-id="element-457">
                      Avg. Mastery Rate
                    </div>
                    <div className="text-4xl font-bold text-gray-900 mb-2" data-id="element-458">
                      65.52%
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-459">
                      Average mastery rate across all attempts
                    </div>
                  </motion.div>

                  <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.45
              }} className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-460">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2" data-id="element-461">
                      Avg. Misinfo Rate
                    </div>
                    <div className="text-4xl font-bold text-gray-900 mb-2" data-id="element-462">
                      18.18%
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-463">
                      Average misinformation rate across all attempts
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </>;
}
function IndividualView({
  learner,
  modulePerformance,
  actionItems,
  COLORS,
  selectedSkill
}) {
  // Filter action items for this specific learner (only non-macro items have learner property)
  const learnerActions = actionItems.filter(item => item.learner && (item.learner.name === learner.name || item.learner.email === learner.email));
  // Filter module performance based on selected skill
  const filteredModulePerformance = selectedSkill === 'All Skills Combined' ? modulePerformance : modulePerformance.filter(m => m.module === selectedSkill);
  // Modal states
  const [messageModal, setMessageModal] = useState<{
    isOpen: boolean;
    item: any | null;
  }>({
    isOpen: false,
    item: null
  });
  const [meetingModal, setMeetingModal] = useState<{
    isOpen: boolean;
    item: any | null;
  }>({
    isOpen: false,
    item: null
  });
  const [kudosModal, setKudosModal] = useState<{
    isOpen: boolean;
    item: any | null;
  }>({
    isOpen: false,
    item: null
  });
  // Get recipient name for modals
  const getRecipientName = (item: any) => {
    if (item.learner) return item.learner.name;
    return 'Team';
  };
  const getRecipientFirstName = (item: any) => {
    if (item.learner) return item.learner.name.split(' ')[0];
    return 'Team';
  };
  // Learner-specific skill data for badges, certificates, assessments, scores, and dates
  const learnerSkillData = {
    // Linda Taylor (id: 1)
    1: {
      'HTML Basics': {
        badgeEarned: true,
        certificateAchieved: true,
        assessments: 2,
        avgScore: 92,
        lastAttempt: '2024-01-12'
      },
      'CSS Fundamentals': {
        badgeEarned: true,
        certificateAchieved: false,
        assessments: 1,
        avgScore: 85,
        lastAttempt: '2024-01-10'
      },
      'JavaScript Basics': {
        badgeEarned: false,
        certificateAchieved: false,
        assessments: 3,
        avgScore: 78,
        lastAttempt: '2024-01-15'
      },
      'Responsive Design': {
        badgeEarned: true,
        certificateAchieved: false,
        assessments: 1,
        avgScore: 88,
        lastAttempt: '2024-01-08'
      }
    },
    // John Smith (id: 2)
    2: {
      'HTML Basics': {
        badgeEarned: true,
        certificateAchieved: false,
        assessments: 2,
        avgScore: 75,
        lastAttempt: '2024-01-11'
      },
      'CSS Fundamentals': {
        badgeEarned: false,
        certificateAchieved: false,
        assessments: 2,
        avgScore: 62,
        lastAttempt: '2024-01-13'
      },
      'JavaScript Basics': {
        badgeEarned: false,
        certificateAchieved: false,
        assessments: 3,
        avgScore: 58,
        lastAttempt: '2024-01-14'
      },
      'Responsive Design': {
        badgeEarned: false,
        certificateAchieved: false,
        assessments: 1,
        avgScore: 70,
        lastAttempt: '2024-01-09'
      }
    },
    // Sarah Johnson (id: 3)
    3: {
      'HTML Basics': {
        badgeEarned: true,
        certificateAchieved: true,
        assessments: 1,
        avgScore: 96,
        lastAttempt: '2024-01-10'
      },
      'CSS Fundamentals': {
        badgeEarned: true,
        certificateAchieved: true,
        assessments: 1,
        avgScore: 94,
        lastAttempt: '2024-01-12'
      },
      'JavaScript Basics': {
        badgeEarned: true,
        certificateAchieved: true,
        assessments: 1,
        avgScore: 95,
        lastAttempt: '2024-01-14'
      },
      'Responsive Design': {
        badgeEarned: true,
        certificateAchieved: true,
        assessments: 1,
        avgScore: 97,
        lastAttempt: '2024-01-16'
      }
    },
    // Michael Chen (id: 4)
    4: {
      'HTML Basics': {
        badgeEarned: true,
        certificateAchieved: false,
        assessments: 2,
        avgScore: 82,
        lastAttempt: '2024-01-08'
      },
      'CSS Fundamentals': {
        badgeEarned: false,
        certificateAchieved: false,
        assessments: 3,
        avgScore: 65,
        lastAttempt: '2024-01-13'
      },
      'JavaScript Basics': {
        badgeEarned: true,
        certificateAchieved: false,
        assessments: 2,
        avgScore: 80,
        lastAttempt: '2024-01-11'
      },
      'Responsive Design': {
        badgeEarned: false,
        certificateAchieved: false,
        assessments: 1,
        avgScore: 72,
        lastAttempt: '2024-01-06'
      }
    },
    // Emily Davis (id: 5)
    5: {
      'HTML Basics': {
        badgeEarned: true,
        certificateAchieved: true,
        assessments: 2,
        avgScore: 88,
        lastAttempt: '2024-01-15'
      },
      'CSS Fundamentals': {
        badgeEarned: true,
        certificateAchieved: false,
        assessments: 1,
        avgScore: 90,
        lastAttempt: '2024-01-12'
      },
      'JavaScript Basics': {
        badgeEarned: true,
        certificateAchieved: false,
        assessments: 2,
        avgScore: 85,
        lastAttempt: '2024-01-14'
      },
      'Responsive Design': {
        badgeEarned: true,
        certificateAchieved: false,
        assessments: 1,
        avgScore: 91,
        lastAttempt: '2024-01-10'
      }
    }
  };
  // Get skill-specific data for current learner
  const currentLearnerSkillData = learnerSkillData[learner.id] || learnerSkillData[1];
  const currentSkillData = selectedSkill !== 'All Skills Combined' ? currentLearnerSkillData[selectedSkill] : null;
  // Learner-specific progress data for charts - different data for each learner
  const learnerProgressData = {
    // Linda Taylor (id: 1) - Strong performer with steady improvement
    1: {
      'All Skills Combined': {
        score: [{
          attempt: 'Attempt 1',
          score: 72
        }, {
          attempt: 'Attempt 2',
          score: 85
        }, {
          attempt: 'Attempt 3',
          score: 92
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 68
        }, {
          attempt: 'Attempt 2',
          mastery: 78
        }, {
          attempt: 'Attempt 3',
          mastery: 88
        }]
      },
      'HTML Basics': {
        score: [{
          attempt: 'Attempt 1',
          score: 85
        }, {
          attempt: 'Attempt 2',
          score: 92
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 88
        }, {
          attempt: 'Attempt 2',
          mastery: 95
        }]
      },
      'CSS Fundamentals': {
        score: [{
          attempt: 'Attempt 1',
          score: 85
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 88
        }]
      },
      'JavaScript Basics': {
        score: [{
          attempt: 'Attempt 1',
          score: 62
        }, {
          attempt: 'Attempt 2',
          score: 70
        }, {
          attempt: 'Attempt 3',
          score: 78
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 55
        }, {
          attempt: 'Attempt 2',
          mastery: 65
        }, {
          attempt: 'Attempt 3',
          mastery: 75
        }]
      },
      'Responsive Design': {
        score: [{
          attempt: 'Attempt 1',
          score: 88
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 90
        }]
      }
    },
    // John Smith (id: 2) - Struggling learner with declining scores in some areas
    2: {
      'All Skills Combined': {
        score: [{
          attempt: 'Attempt 1',
          score: 65
        }, {
          attempt: 'Attempt 2',
          score: 68
        }, {
          attempt: 'Attempt 3',
          score: 72
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 55
        }, {
          attempt: 'Attempt 2',
          mastery: 60
        }, {
          attempt: 'Attempt 3',
          mastery: 65
        }]
      },
      'HTML Basics': {
        score: [{
          attempt: 'Attempt 1',
          score: 68
        }, {
          attempt: 'Attempt 2',
          score: 75
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 62
        }, {
          attempt: 'Attempt 2',
          mastery: 72
        }]
      },
      'CSS Fundamentals': {
        score: [{
          attempt: 'Attempt 1',
          score: 55
        }, {
          attempt: 'Attempt 2',
          score: 62
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 48
        }, {
          attempt: 'Attempt 2',
          mastery: 58
        }]
      },
      'JavaScript Basics': {
        score: [{
          attempt: 'Attempt 1',
          score: 68
        }, {
          attempt: 'Attempt 2',
          score: 65
        }, {
          attempt: 'Attempt 3',
          score: 58
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 60
        }, {
          attempt: 'Attempt 2',
          mastery: 55
        }, {
          attempt: 'Attempt 3',
          mastery: 50
        }]
      },
      'Responsive Design': {
        score: [{
          attempt: 'Attempt 1',
          score: 70
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 65
        }]
      }
    },
    // Sarah Johnson (id: 3) - High performer, first-attempt success
    3: {
      'All Skills Combined': {
        score: [{
          attempt: 'Attempt 1',
          score: 95
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 94
        }]
      },
      'HTML Basics': {
        score: [{
          attempt: 'Attempt 1',
          score: 96
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 95
        }]
      },
      'CSS Fundamentals': {
        score: [{
          attempt: 'Attempt 1',
          score: 94
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 92
        }]
      },
      'JavaScript Basics': {
        score: [{
          attempt: 'Attempt 1',
          score: 95
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 93
        }]
      },
      'Responsive Design': {
        score: [{
          attempt: 'Attempt 1',
          score: 97
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 96
        }]
      }
    },
    // Michael Chen (id: 4) - Mixed performance, struggles with CSS
    4: {
      'All Skills Combined': {
        score: [{
          attempt: 'Attempt 1',
          score: 70
        }, {
          attempt: 'Attempt 2',
          score: 75
        }, {
          attempt: 'Attempt 3',
          score: 78
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 62
        }, {
          attempt: 'Attempt 2',
          mastery: 68
        }, {
          attempt: 'Attempt 3',
          mastery: 72
        }]
      },
      'HTML Basics': {
        score: [{
          attempt: 'Attempt 1',
          score: 75
        }, {
          attempt: 'Attempt 2',
          score: 82
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 70
        }, {
          attempt: 'Attempt 2',
          mastery: 80
        }]
      },
      'CSS Fundamentals': {
        score: [{
          attempt: 'Attempt 1',
          score: 52
        }, {
          attempt: 'Attempt 2',
          score: 58
        }, {
          attempt: 'Attempt 3',
          score: 65
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 45
        }, {
          attempt: 'Attempt 2',
          mastery: 52
        }, {
          attempt: 'Attempt 3',
          mastery: 60
        }]
      },
      'JavaScript Basics': {
        score: [{
          attempt: 'Attempt 1',
          score: 72
        }, {
          attempt: 'Attempt 2',
          score: 80
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 68
        }, {
          attempt: 'Attempt 2',
          mastery: 78
        }]
      },
      'Responsive Design': {
        score: [{
          attempt: 'Attempt 1',
          score: 72
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 68
        }]
      }
    },
    // Emily Davis (id: 5) - Good performer with significant improvement
    5: {
      'All Skills Combined': {
        score: [{
          attempt: 'Attempt 1',
          score: 65
        }, {
          attempt: 'Attempt 2',
          score: 88
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 60
        }, {
          attempt: 'Attempt 2',
          mastery: 85
        }]
      },
      'HTML Basics': {
        score: [{
          attempt: 'Attempt 1',
          score: 65
        }, {
          attempt: 'Attempt 2',
          score: 88
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 60
        }, {
          attempt: 'Attempt 2',
          mastery: 92
        }]
      },
      'CSS Fundamentals': {
        score: [{
          attempt: 'Attempt 1',
          score: 90
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 88
        }]
      },
      'JavaScript Basics': {
        score: [{
          attempt: 'Attempt 1',
          score: 78
        }, {
          attempt: 'Attempt 2',
          score: 85
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 72
        }, {
          attempt: 'Attempt 2',
          mastery: 82
        }]
      },
      'Responsive Design': {
        score: [{
          attempt: 'Attempt 1',
          score: 91
        }],
        mastery: [{
          attempt: 'Attempt 1',
          mastery: 89
        }]
      }
    }
  };
  // Get the appropriate data for the selected learner and skill
  const currentLearnerProgressData = learnerProgressData[learner.id] || learnerProgressData[1];
  const currentSkillProgressData = currentLearnerProgressData[selectedSkill] || currentLearnerProgressData['All Skills Combined'];
  const scoreProgressData = currentSkillProgressData.score;
  const masteryProgressData = currentSkillProgressData.mastery;
  return <>
      {/* Learner Header */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6" data-id="element-464">
        <div className="flex items-center justify-between" data-id="element-465">
          <div className="flex items-center gap-4" data-id="element-466">
            <div className="w-16 h-16 rounded-full bg-[#30718D] flex items-center justify-center text-white text-xl font-bold shadow-sm" data-id="element-467">
              {learner.name.split(' ').map(n => n[0]).join('')}
            </div>
            <div data-id="element-468">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-469">
                {learner.name}
              </h2>
              <p className="text-gray-600 text-sm" data-id="element-470">{learner.email}</p>
            </div>
          </div>
          <div className="flex gap-4" data-id="element-471">
            {/* Badge - Conditional based on skill selection */}
            <div className="text-center px-6 py-3 bg-gray-50 rounded-lg border border-gray-100" data-id="element-472">
              <div className="text-xs font-medium text-gray-500 mb-1" data-id="element-473">
                {currentSkillData ? 'Badge Earned' : 'Badges Earned'}
              </div>
              {currentSkillData ? <div className={`text-lg font-bold ${currentSkillData.badgeEarned ? 'text-[#007558]' : 'text-gray-400'}`} data-id="element-474">
                  {currentSkillData.badgeEarned ? 'Yes' : 'No'}
                </div> : <div className="text-2xl font-bold text-gray-900" data-id="element-475">12</div>}
            </div>

            {/* Certificate - Conditional based on skill selection */}
            <div className="text-center px-6 py-3 bg-gray-50 rounded-lg border border-gray-100" data-id="element-476">
              <div className="text-xs font-medium text-gray-500 mb-1" data-id="element-477">
                {currentSkillData ? 'Certificate Achieved' : 'Certificates Achieved'}
              </div>
              {currentSkillData ? <div className={`text-lg font-bold ${currentSkillData.certificateAchieved ? 'text-[#007558]' : 'text-gray-400'}`} data-id="element-478">
                  {currentSkillData.certificateAchieved ? 'Yes' : 'No'}
                </div> : <div className="text-2xl font-bold text-gray-900" data-id="element-479">3</div>}
            </div>

            {/* Total Assessments - Updates based on skill */}
            <div className="text-center px-6 py-3 bg-gray-50 rounded-lg border border-gray-100" data-id="element-480">
              <div className="text-xs font-medium text-gray-500 mb-1" data-id="element-481">
                Total Assessments
              </div>
              <div className="text-2xl font-bold text-gray-900" data-id="element-482">
                {currentSkillData ? currentSkillData.assessments : learner.assessments}
              </div>
            </div>

            {/* Average Score - Updates based on skill */}
            <div className="text-center px-6 py-3 bg-gray-50 rounded-lg border border-gray-100" data-id="element-483">
              <div className="text-xs font-medium text-gray-500 mb-1" data-id="element-484">
                Average Score
              </div>
              <div className="text-2xl font-bold text-[#30718D]" data-id="element-485">
                {currentSkillData ? currentSkillData.avgScore : learner.avgScore}
                %
              </div>
            </div>

            {/* Last Attempt / Attempted Date - Updates based on skill */}
            <div className="text-center px-6 py-3 bg-gray-50 rounded-lg border border-gray-100" data-id="element-486">
              <div className="text-xs font-medium text-gray-500 mb-1" data-id="element-487">
                {currentSkillData ? 'Attempted Date' : 'Last Attempt'}
              </div>
              <div className="text-lg font-semibold text-gray-900" data-id="element-488">
                {currentSkillData ? currentSkillData.lastAttempt : learner.lastAttempt}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Individual Learner Progress Section */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6" data-id="element-489">
        <div className="flex items-start justify-between mb-6" data-id="element-490">
          <div data-id="element-491">
            <h3 className="text-xl font-bold text-gray-900 mb-1" data-id="element-492">
              Individual Learner Progress
            </h3>
            <p className="text-sm text-gray-600" data-id="element-493">
              Track attempt-by-attempt progress for {learner.name.split(' ')[0]}{' '}
              in {selectedSkill}
            </p>
          </div>
          {selectedSkill !== 'All Skills Combined' && <span className="px-3 py-1 bg-[#30718D]/10 text-[#30718D] rounded-full text-sm font-medium" data-id="element-494">
              Viewing: {selectedSkill}
            </span>}
        </div>

        {/* Progress Charts */}
        <div className="grid grid-cols-2 gap-6" data-id="element-495">
          {/* Score Progress Chart */}
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200" data-id="element-496">
            <h4 className="text-base font-bold text-gray-900 text-center mb-6" data-id="element-497">
              Score Progress
            </h4>
            <ResponsiveContainer width="100%" height={400} data-id="element-498">
              <LineChart data={scoreProgressData} margin={{
              bottom: 20
            }} data-id="element-499">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" data-id="element-500" />
                <XAxis dataKey="attempt" label={{
                value: 'Attempts',
                position: 'insideBottom',
                offset: -10
              }} tick={{
                fontSize: 12
              }} data-id="element-501" />
                <YAxis domain={[0, 100]} label={{
                value: 'Percentage (%)',
                angle: -90,
                position: 'insideLeft'
              }} tick={{
                fontSize: 12
              }} data-id="element-502" />
                <Tooltip data-id="element-503" />
                <Line type="monotone" dataKey="score" stroke="#30718D" strokeWidth={3} dot={{
                fill: '#30718D',
                r: 6
              }} activeDot={{
                r: 8
              }} data-id="element-504" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Mastery Rate Progress Chart */}
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200" data-id="element-505">
            <h4 className="text-base font-bold text-gray-900 text-center mb-6" data-id="element-506">
              Mastery Rate Progress
            </h4>
            <ResponsiveContainer width="100%" height={400} data-id="element-507">
              <LineChart data={masteryProgressData} margin={{
              bottom: 20
            }} data-id="element-508">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" data-id="element-509" />
                <XAxis dataKey="attempt" label={{
                value: 'Attempts',
                position: 'insideBottom',
                offset: -10
              }} tick={{
                fontSize: 12
              }} data-id="element-510" />
                <YAxis domain={[0, 100]} label={{
                value: 'Percentage (%)',
                angle: -90,
                position: 'insideLeft'
              }} tick={{
                fontSize: 12
              }} data-id="element-511" />
                <Tooltip data-id="element-512" />
                <Line type="monotone" dataKey="mastery" stroke="#007558" strokeWidth={3} dot={{
                fill: '#007558',
                r: 6
              }} activeDot={{
                r: 8
              }} data-id="element-513" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recommended Actions Section */}
      <AnimatePresence mode="wait" data-id="element-514">
        {learnerActions.length > 0 && <motion.div key={learner.id} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.3
      }} className="mb-6" data-id="element-515">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-516">
              <div className="flex items-center gap-3 mb-5" data-id="element-517">
                <div className="w-10 h-10 rounded-lg bg-[#30718D] flex items-center justify-center shadow-sm" data-id="element-518">
                  <Zap size={20} className="text-white" data-id="element-519" />
                </div>
                <div data-id="element-520">
                  <h3 className="text-lg font-bold text-gray-900" data-id="element-521">
                    Recommended Actions
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-522">
                    Based on {learner.name.split(' ')[0]}'s performance
                  </p>
                </div>
              </div>

              <div className="space-y-4" data-id="element-523">
                {learnerActions.map((item, index) => <motion.div key={item.id} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.1
            }} className="bg-gray-50 rounded-xl p-6 border border-gray-200" data-id="element-524">
                    {/* Header Row */}
                    <div className="flex items-start justify-between mb-1" data-id="element-525">
                      <div className="flex items-center gap-4" data-id="element-526">
                        {/* Icon */}
                        <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${item.priority === 'high' ? 'bg-[#DB3725]/10 border-2 border-[#DB3725]' : item.priority === 'medium' ? 'bg-[#F59E0B]/10 border-2 border-[#F59E0B]' : 'bg-[#007558]/10 border-2 border-[#007558]'}`} data-id="element-527">
                          {item.priority === 'high' ? <AlertTriangle size={24} className="text-[#DB3725]" data-id="element-528" /> : item.priority === 'medium' ? <Clock size={24} className="text-[#F59E0B]" data-id="element-529" /> : <Star size={24} className="text-[#007558]" data-id="element-530" />}
                        </div>

                        {/* Name and Badge */}
                        <div data-id="element-531">
                          <div className="flex items-center gap-3" data-id="element-532">
                            <h4 className="text-lg font-semibold text-gray-900" data-id="element-533">
                              {item.learner ? item.learner.name : 'System Alert'}
                            </h4>
                            <span className={`px-3 py-1 rounded text-xs font-semibold ${item.priority === 'high' ? 'bg-[#DB3725] text-white' : item.priority === 'medium' ? 'bg-white text-gray-900 border border-gray-900' : 'bg-white text-gray-900 border border-gray-900'}`} data-id="element-534">
                              {item.priority === 'high' ? 'Urgent' : item.priority === 'medium' ? 'Attention' : 'Celebrate'}
                            </span>
                          </div>
                          {item.learner && <p className="text-sm text-gray-500 mt-0.5" data-id="element-535">
                              {item.learner.email}
                            </p>}
                        </div>
                      </div>

                      {/* Module and Time */}
                      <div className="text-right" data-id="element-536">
                        <p className="font-semibold text-gray-900" data-id="element-537">
                          {item.module}
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-538">
                          {item.lastActivity}
                        </p>
                      </div>
                    </div>

                    {/* Insight */}
                    <div className="flex items-start gap-2 mt-4 mb-2" data-id="element-539">
                      <Zap size={18} className="text-gray-700 mt-0.5 flex-shrink-0" data-id="element-540" />
                      <p className="font-semibold text-gray-900" data-id="element-541">
                        {item.insight}
                      </p>
                    </div>

                    {/* Details */}
                    <p className="text-sm text-gray-600 ml-6 mb-5 leading-relaxed" data-id="element-542">
                      {item.details}
                    </p>

                    {/* Suggested Actions */}
                    <div className="mb-5" data-id="element-543">
                      <p className="text-sm font-semibold text-gray-700 mb-3" data-id="element-544">
                        Suggested Actions
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-545">
                        {item.suggestedActions.map((action, idx) => <span key={idx} className="px-4 py-2 bg-white text-gray-800 rounded text-sm font-medium border border-gray-200" data-id="element-546">
                            {action}
                          </span>)}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-3 pt-5 border-t border-gray-200" data-id="element-547">
                      <button onClick={() => setMessageModal({
                  isOpen: true,
                  item
                })} className="flex items-center gap-2 px-5 py-2.5 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-all text-sm font-medium" data-id="element-548">
                        <Mail size={16} data-id="element-549" />
                        <span data-id="element-550">Send Message</span>
                      </button>
                      <button onClick={() => setMeetingModal({
                  isOpen: true,
                  item
                })} className="flex items-center gap-2 px-5 py-2.5 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-all text-sm font-medium" data-id="element-551">
                        <Video size={16} data-id="element-552" />
                        <span data-id="element-553">Schedule Meeting</span>
                      </button>
                      {item.priority === 'positive' && <button onClick={() => setKudosModal({
                  isOpen: true,
                  item
                })} className="flex items-center gap-2 px-5 py-2.5 border border-[#007558] bg-[#007558]/10 text-[#007558] rounded-lg hover:bg-[#007558]/20 transition-all text-sm font-medium" data-id="element-554">
                          <ThumbsUp size={16} data-id="element-555" />
                          <span data-id="element-556">Send Kudos</span>
                        </button>}
                      <button className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors" data-id="element-557">
                        Dismiss
                      </button>
                    </div>
                  </motion.div>)}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Individual Module Performance */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6" data-id="element-558">
        <div className="flex items-center justify-between mb-5" data-id="element-559">
          <h3 className="text-lg font-bold text-gray-900" data-id="element-560">
            {selectedSkill === 'All Skills Combined' ? 'Module Performance' : `${selectedSkill} Performance`}
          </h3>
          {selectedSkill !== 'All Skills Combined' && <span className="px-3 py-1 bg-[#30718D]/10 text-[#30718D] rounded-full text-sm font-medium" data-id="element-561">
              Filtered
            </span>}
        </div>
        <div className="space-y-4" data-id="element-562">
          {filteredModulePerformance.length > 0 ? filteredModulePerformance.map((module, index) => <div key={index} className="bg-gray-50 rounded-lg p-5 border border-gray-200" data-id="element-563">
                <div className="flex items-center justify-between mb-4" data-id="element-564">
                  <div data-id="element-565">
                    <h4 className="font-semibold text-base text-gray-900" data-id="element-566">
                      {module.module}
                    </h4>
                    <p className="text-sm text-gray-500 mt-0.5" data-id="element-567">
                      {module.attempts} attempt
                      {module.attempts !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <div className="text-right" data-id="element-568">
                    <div className="text-3xl font-bold text-[#30718D]" data-id="element-569">
                      {module.score}%
                    </div>
                    <div className="text-xs font-medium text-gray-500 mt-0.5" data-id="element-570">
                      Overall Score
                    </div>
                  </div>
                </div>

                {/* Mastery vs Misinformation Visual */}
                <div className="grid grid-cols-2 gap-4" data-id="element-571">
                  <div data-id="element-572">
                    <div className="flex items-center justify-between mb-2" data-id="element-573">
                      <span className="text-sm font-semibold text-gray-700" data-id="element-574">
                        Mastery
                      </span>
                      <span className="text-sm font-bold text-[#007558]" data-id="element-575">
                        {module.mastery}%
                      </span>
                    </div>
                    <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-576">
                      <div className="h-full bg-[#007558] rounded-full transition-all duration-500" style={{
                  width: `${module.mastery}%`
                }} data-id="element-577" />
                    </div>
                  </div>
                  <div data-id="element-578">
                    <div className="flex items-center justify-between mb-2" data-id="element-579">
                      <span className="text-sm font-semibold text-gray-700" data-id="element-580">
                        Misinformation
                      </span>
                      <span className="text-sm font-bold text-[#DB3725]" data-id="element-581">
                        {module.misinformation}%
                      </span>
                    </div>
                    <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-582">
                      <div className="h-full bg-[#DB3725] rounded-full transition-all duration-500" style={{
                  width: `${module.misinformation}%`
                }} data-id="element-583" />
                    </div>
                  </div>
                </div>
              </div>) : <div className="text-center py-8 text-gray-500" data-id="element-584">
              No performance data available for {selectedSkill}.
            </div>}
        </div>
      </div>

      {/* Enhanced Mastery Heatmap */}
      <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100" data-id="element-585">
        <div className="mb-6" data-id="element-586">
          <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-587">
            Knowledge Mastery Overview
          </h3>
          <p className="text-gray-600 text-sm" data-id="element-588">
            Visual representation of {learner.name.split(' ')[0]}'s proficiency
            {selectedSkill === 'All Skills Combined' ? ' across all modules' : ` in ${selectedSkill}`}
          </p>
        </div>

        {filteredModulePerformance.length > 0 ? <div className={`grid gap-4 mb-8 ${filteredModulePerformance.length === 1 ? 'grid-cols-1 max-w-xs' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'}`} data-id="element-589">
            {filteredModulePerformance.map((module, index) => {
          const masteryLevel = module.mastery >= 90 ? 'expert' : module.mastery >= 75 ? 'proficient' : module.mastery >= 60 ? 'developing' : 'needs-work';
          const colorConfig = {
            expert: {
              bg: 'from-[#007558] to-[#005a44]',
              border: 'border-[#007558]',
              text: 'text-white',
              icon: '★'
            },
            proficient: {
              bg: 'from-[#30718D] to-[#265a70]',
              border: 'border-[#30718D]',
              text: 'text-white',
              icon: '✓'
            },
            developing: {
              bg: 'from-[#F59E0B] to-[#D97706]',
              border: 'border-[#F59E0B]',
              text: 'text-white',
              icon: '↗'
            },
            'needs-work': {
              bg: 'from-[#DB3725] to-[#b82e1f]',
              border: 'border-[#DB3725]',
              text: 'text-white',
              icon: '!'
            }
          };
          const config = colorConfig[masteryLevel];
          return <motion.div key={index} initial={{
            opacity: 0,
            scale: 0.9,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} transition={{
            delay: index * 0.1,
            type: 'spring',
            stiffness: 200,
            damping: 20
          }} whileHover={{
            scale: 1.05,
            y: -4,
            transition: {
              duration: 0.2
            }
          }} className="group relative cursor-pointer" data-id="element-590">
                  <div className={`
                    relative overflow-hidden rounded-xl border-2 ${config.border}
                    bg-gradient-to-br ${config.bg}
                    shadow-md hover:shadow-xl
                    transition-all duration-300
                    aspect-square
                  `} data-id="element-591">
                    {/* Shine effect on hover */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/10 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-592" />

                    {/* Content */}
                    <div className="relative h-full flex flex-col items-center justify-center p-6 text-center" data-id="element-593">
                      {/* Attempts Badge */}
                      <motion.div className="absolute top-3 right-3" initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  delay: index * 0.1 + 0.3,
                  type: 'spring'
                }} data-id="element-594">
                        <div className="bg-white/20 backdrop-blur-sm rounded-full px-2.5 py-1" data-id="element-595">
                          <span className={`text-xs font-bold ${config.text}`} data-id="element-596">
                            Attempts: {module.attempts}
                          </span>
                        </div>
                      </motion.div>

                      {/* Score */}
                      <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  delay: index * 0.1 + 0.2,
                  type: 'spring',
                  stiffness: 300
                }} className="mb-3" data-id="element-597">
                        <div className={`text-5xl font-black ${config.text} drop-shadow-lg`} data-id="element-598">
                          {module.mastery}%
                        </div>
                      </motion.div>

                      {/* Module Name */}
                      <div className={`text-sm font-bold ${config.text} drop-shadow-md leading-tight px-2`} data-id="element-599">
                        {module.module}
                      </div>

                      {/* Progress Ring */}
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 w-16 h-1 bg-white/30 rounded-full overflow-hidden" data-id="element-600">
                        <motion.div initial={{
                    width: 0
                  }} animate={{
                    width: `${module.mastery}%`
                  }} transition={{
                    delay: index * 0.1 + 0.4,
                    duration: 0.8
                  }} className="h-full bg-white/90 rounded-full" data-id="element-601" />
                      </div>
                    </div>

                    {/* Hover Tooltip */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl p-6 flex flex-col justify-center bg-white shadow-xl" data-id="element-602">
                      <div className="space-y-3" data-id="element-603">
                        <div className="text-base font-bold border-b border-gray-200 pb-2 text-gray-900" data-id="element-604">
                          {module.module}
                        </div>

                        <div className="space-y-2 text-sm" data-id="element-605">
                          <div className="flex justify-between items-center" data-id="element-606">
                            <span className="text-gray-600" data-id="element-607">
                              Overall Score:
                            </span>
                            <span className="font-bold text-gray-900" data-id="element-608">
                              {module.score}%
                            </span>
                          </div>

                          <div className="flex justify-between items-center" data-id="element-609">
                            <span className="text-gray-600" data-id="element-610">Mastery:</span>
                            <span className="font-bold text-[#007558]" data-id="element-611">
                              {module.mastery}%
                            </span>
                          </div>

                          <div className="flex justify-between items-center" data-id="element-612">
                            <span className="text-gray-600" data-id="element-613">
                              Misinformation:
                            </span>
                            <span className="font-bold text-[#DB3725]" data-id="element-614">
                              {module.misinformation}%
                            </span>
                          </div>

                          <div className="flex justify-between items-center pt-2 border-t border-gray-200" data-id="element-615">
                            <span className="text-gray-600" data-id="element-616">Attempts:</span>
                            <span className="font-bold text-[#30718D]" data-id="element-617">
                              {module.attempts}
                            </span>
                          </div>
                        </div>

                        <div className="pt-2" data-id="element-618">
                          <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border ${masteryLevel === 'expert' ? 'bg-[#007558]/30 text-[#007558] border-[#007558]/50' : masteryLevel === 'proficient' ? 'bg-[#30718D]/30 text-[#30718D] border-[#30718D]/50' : masteryLevel === 'developing' ? 'bg-[#F59E0B]/30 text-[#F59E0B] border-[#F59E0B]/50' : 'bg-[#DB3725]/30 text-[#DB3725] border-[#DB3725]/50'}`} data-id="element-619">
                            <span data-id="element-620">{config.icon}</span>
                            <span data-id="element-621">
                              {masteryLevel === 'expert' ? 'Expert Level' : masteryLevel === 'proficient' ? 'Proficient' : masteryLevel === 'developing' ? 'Developing' : 'Needs Focus'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>;
        })}
          </div> : <div className="text-center py-8 text-gray-500" data-id="element-622">
            No performance data available for {selectedSkill}.
          </div>}

        {/* Enhanced Legend */}
        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200" data-id="element-623">
          <h4 className="text-sm font-bold text-gray-700 mb-4 uppercase tracking-wide" data-id="element-624">
            Mastery Levels
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-625">
            <div className="flex items-center gap-3" data-id="element-626">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#DB3725] to-[#b82e1f] shadow-md flex items-center justify-center flex-shrink-0" data-id="element-627">
                <span className="text-white font-bold text-lg" data-id="element-628">!</span>
              </div>
              <div data-id="element-629">
                <div className="font-bold text-gray-900 text-sm" data-id="element-630">
                  Needs Work
                </div>
                <div className="text-xs text-gray-500" data-id="element-631">0-59%</div>
              </div>
            </div>

            <div className="flex items-center gap-3" data-id="element-632">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#F59E0B] to-[#D97706] shadow-md flex items-center justify-center flex-shrink-0" data-id="element-633">
                <span className="text-white font-bold text-lg" data-id="element-634">↗</span>
              </div>
              <div data-id="element-635">
                <div className="font-bold text-gray-900 text-sm" data-id="element-636">
                  Developing
                </div>
                <div className="text-xs text-gray-500" data-id="element-637">60-74%</div>
              </div>
            </div>

            <div className="flex items-center gap-3" data-id="element-638">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#30718D] to-[#265a70] shadow-md flex items-center justify-center flex-shrink-0" data-id="element-639">
                <span className="text-white font-bold text-lg" data-id="element-640">✓</span>
              </div>
              <div data-id="element-641">
                <div className="font-bold text-gray-900 text-sm" data-id="element-642">
                  Proficient
                </div>
                <div className="text-xs text-gray-500" data-id="element-643">75-89%</div>
              </div>
            </div>

            <div className="flex items-center gap-3" data-id="element-644">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#007558] to-[#005a44] shadow-md flex items-center justify-center flex-shrink-0" data-id="element-645">
                <span className="text-white font-bold text-lg" data-id="element-646">★</span>
              </div>
              <div data-id="element-647">
                <div className="font-bold text-gray-900 text-sm" data-id="element-648">Mastery</div>
                <div className="text-xs text-gray-500" data-id="element-649">90-100%</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>;
}
function MetricCard({
  icon,
  label,
  value,
  change,
  changeLabel,
  color
}) {
  const colorClasses = {
    blue: 'bg-[#30718D]/10 text-[#30718D]',
    green: 'bg-[#007558]/10 text-[#007558]',
    purple: 'bg-[#30718D]/10 text-[#30718D]',
    orange: 'bg-orange-100 text-orange-600'
  };
  const isPositive = change > 0;
  const isNeutral = change === 0;
  return <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-650">
      <div className="flex items-center justify-between mb-2" data-id="element-651">
        <span className="text-sm font-medium text-gray-600" data-id="element-652">{label}</span>
        {isPositive ? <TrendingUp size={20} className="text-green-600" data-id="element-653" /> : isNeutral ? <Minus size={20} className="text-gray-400" data-id="element-654" /> : <TrendingUp size={20} className="text-red-600 rotate-180" data-id="element-655" />}
      </div>
      <div className="flex items-end justify-between" data-id="element-656">
        <div className="text-3xl font-bold text-gray-900" data-id="element-657">{value}</div>
        <span className={`text-lg font-bold ${isPositive ? 'text-green-600' : isNeutral ? 'text-gray-500' : 'text-red-600'}`} data-id="element-658">
          {isPositive ? '+' : ''}
          {change}%
        </span>
      </div>
    </div>;
}